import { RecordingState } from '../RecordingState';
import { Logger } from '../../support/Logger';
import { ICareMapNoteTarget } from '../../notes/model/ICareMapNoteTarget';
import { IAudioRejectionReason, resolveAudioMimeType, validateAudioFile } from './AudioFileValidator';
import { readAudioDurationMs } from './readAudioDuration';

export interface IAudioUploadRejection {
    fileName: string;
    reason: IAudioRejectionReason;
}

export interface IAudioUploadResult {
    addedCount: number;
    rejected: IAudioUploadRejection[];
}

/**
 * Turns uploaded audio files into notes by routing each one through the recording pipeline
 * (RecordingState.add) — the same path the live recorder uses. Going through RecordingState
 * is deliberate: it is what drives quota counting, the paywall, transcription, and the summary
 * display. One note is created per accepted file; rejected files are reported back to the caller.
 */
export class AudioUploadService {
    static inject = () => [RecordingState, Logger];
    constructor(
        private recordingState: RecordingState,
        private logger: Logger
    ) {}

    uploadFiles = async (files: File[], noteTarget?: ICareMapNoteTarget): Promise<IAudioUploadResult> => {
        const rejected: IAudioUploadRejection[] = [];
        let addedCount = 0;

        for (const file of files) {
            const validation = validateAudioFile(file);
            if (!validation.ok) {
                rejected.push({ fileName: file.name, reason: validation.reason });
                continue;
            }

            try {
                const msDuration = await readAudioDurationMs(file);
                const arrayBuffer = await file.arrayBuffer();
                await this.recordingState.add({ file: arrayBuffer, mimeType: resolveAudioMimeType(file), msDuration, noteTarget, isUpload: true }, true);
                addedCount++;
            } catch (error) {
                this.logger.error('Failed to create note from uploaded audio file', { error, fileName: file.name });
                rejected.push({ fileName: file.name, reason: 'upload-failed' });
            }
        }

        return { addedCount, rejected };
    };
}
